/**
 * Chiamate al backend per account e abbonamenti.
 *
 * Tutto passa da `chiama`, che manda e riceve JSON e traduce gli errori in
 * un `Error` con `status` e `codice`, cosi' le pagine mostrano il messaggio
 * del server (che e' gia' in italiano e pensato per l'utente) e sanno
 * distinguere un 401 (sessione assente) da un guasto.
 *
 * La `fetch` e' iniettabile per le prove, come in `jobPolling.js`.
 */

/**
 * @param {Response} res
 * @param {any} dati corpo gia' decodificato, se c'era
 */
export function messaggioErrore(res, dati) {
  const dal = dati?.detail;
  if (typeof dal === "string" && dal) return dal;
  if (res.status === 401) return "Accesso richiesto: entra con il tuo account.";
  if (res.status === 429) return "Troppi tentativi: riprova tra qualche minuto.";
  if (res.status >= 500) return "Il servizio non ha risposto. Riprova fra un minuto.";
  return `Errore ${res.status}`;
}

/**
 * @param {(input: string, init?: RequestInit) => Promise<Response>} [fetchImpl]
 */
export function creaAccountApi(fetchImpl = (...a) => fetch(...a)) {
  async function chiama(metodo, percorso, corpo) {
    const opzioni = { method: metodo, headers: {}, credentials: "same-origin" };
    if (corpo !== undefined) {
      opzioni.headers["Content-Type"] = "application/json";
      opzioni.body = JSON.stringify(corpo);
    }
    const res = await fetchImpl(percorso, opzioni);
    const dati = await res.json().catch(() => ({}));
    if (!res.ok) {
      const errore = new Error(messaggioErrore(res, dati));
      errore.status = res.status;
      errore.codice = dati?.codice;
      throw errore;
    }
    return dati;
  }

  return {
    /** Profilo dell'utente in sessione; `null` se non c'e' una sessione. */
    async io() {
      try {
        return await chiama("GET", "/api/account/io");
      } catch (e) {
        if (e.status === 401) return null;
        throw e;
      }
    },
    registrazione: ({ email, password, nome, azienda }) =>
      chiama("POST", "/api/account/registrazione", { email, password, nome, azienda }),
    verifica: (token) => chiama("POST", "/api/account/verifica", { token }),
    reinviaVerifica: (email) => chiama("POST", "/api/account/verifica/reinvia", { email }),
    accesso: (email, password) => chiama("POST", "/api/account/accesso", { email, password }),
    uscita: () => chiama("POST", "/api/account/uscita", {}),
    richiediReset: (email) => chiama("POST", "/api/account/password/reset-richiesta", { email }),
    reimpostaPassword: (token, password) =>
      chiama("POST", "/api/account/password/reset", { token, password }),
    cambiaPassword: (vecchia, nuova) => chiama("POST", "/api/account/password", { vecchia, nuova }),
    invita: (email) => chiama("POST", "/api/account/utenti/invito", { email }),
    revocaInvito: (email) => chiama("POST", "/api/account/utenti/invito/revoca", { email }),
    infoInvito: (token) =>
      chiama("GET", `/api/account/invito?${new URLSearchParams({ token }).toString()}`),
    accettaInvito: (token, nome, password) =>
      chiama("POST", "/api/account/invito/accetta", { token, nome, password }),
    rimuoviUtente: (id) => chiama("DELETE", `/api/account/utenti/${encodeURIComponent(id)}`),
    // Diritti sui dati (GDPR): rettifica, cambio d'indirizzo, cancellazione, copia.
    // I campi assenti non viaggiano (JSON.stringify salta gli undefined):
    // il backend cambia solo quelli ricevuti.
    aggiornaProfilo: ({ nome, azienda, avvisiDocumenti }) =>
      chiama("POST", "/api/account/profilo", { nome, azienda, avvisi_documenti: avvisiDocumenti }),
    cambiaEmail: (email, password) => chiama("POST", "/api/account/email", { email, password }),
    confermaEmail: (token) => chiama("POST", "/api/account/email/conferma", { token }),
    elimina: (password) => chiama("POST", "/api/account/elimina", { password }),
    // Parole salvate: la lista personale (piani Silver e superiori).
    parole: () => chiama("GET", "/api/account/parole"),
    aggiungiParole: (parole) => chiama("POST", "/api/account/parole", { parole }),
    rimuoviParola: (id) => chiama("DELETE", `/api/account/parole/${encodeURIComponent(id)}`),
    /** La copia dei propri dati: restituisce nome del file e contenuto (Blob). */
    async esporta() {
      const res = await fetchImpl("/api/account/esporta", { method: "GET", credentials: "same-origin" });
      if (!res.ok) {
        const dati = await res.json().catch(() => ({}));
        const errore = new Error(messaggioErrore(res, dati));
        errore.status = res.status;
        throw errore;
      }
      const disposizione = res.headers?.get?.("content-disposition") || "";
      const nome = /filename="([^"]+)"/.exec(disposizione)?.[1] || "obscura-dati.json";
      return { nome, contenuto: await res.blob() };
    },
    /** I lavori dell'azienda ancora vivi nel motore (circa un'ora). */
    lavori: () => chiama("GET", "/api/account/lavori"),
    piani: () => chiama("GET", "/api/abbonamenti/piani"),
    checkout: (piano, cadenza) => chiama("POST", "/api/abbonamenti/checkout", { piano, cadenza }),
    portale: () => chiama("POST", "/api/abbonamenti/portale", {}),
    sincronizza: () => chiama("POST", "/api/abbonamenti/sincronizza", {}),
  };
}

export const accountApi = creaAccountApi();
